import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, MessageCircle } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { GoldButton, OutlineButton, SectionLabel, waLink } from "@/components/ui-kit";

export const PageHero = ({ label, title, subtitle, text, image, height = "h-[62vh] min-h-[460px]" }) => (
  <section className={`relative ${height} flex items-end grain overflow-hidden`} data-testid="page-hero">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
    <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 lg:px-10 pb-16">
      <Reveal><SectionLabel dark>{label}</SectionLabel></Reveal>
      <h1 className="mt-5 text-ivory font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
        <MaskLine delay={0.1}>{title}</MaskLine>
      </h1>
      {subtitle && <Reveal delay={0.25}><p className="mt-4 text-gold text-lg lg:text-xl font-medium">{subtitle}</p></Reveal>}
      {text && <Reveal delay={0.35}><p className="mt-5 text-ivory/70 max-w-2xl leading-relaxed">{text}</p></Reveal>}
    </div>
  </section>
);

export const FinalCTA = ({
  title = "Inizia il tuo percorso con Body Evolution 2.0",
  text = "Che tu voglia tornare in forma, migliorare la tua performance, allenarti con costanza o ritrovare equilibrio, il nostro staff è pronto ad accompagnarti passo dopo passo.",
  primary = "Prenota la tua prova gratuita",
}) => (
  <section className="relative bg-ink text-ivory grain overflow-hidden" data-testid="final-cta">
    <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32 text-center flex flex-col items-center">
      <Reveal><SectionLabel dark className="justify-center">Il tuo percorso inizia qui</SectionLabel></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl">{title}</h2>
      </Reveal>
      <Reveal delay={0.2}><p className="mt-6 text-ivory/65 max-w-2xl leading-relaxed">{text}</p></Reveal>
      <Reveal delay={0.3}>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <GoldButton to="/contatti"><CalendarCheck size={18} /> {primary}</GoldButton>
          <OutlineButton href={waLink()} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Scrivici su WhatsApp</OutlineButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Marquee = ({ items }) => (
  <div className="bg-ink border-y border-white/10 py-6 overflow-hidden" data-testid="marquee">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...items, ...items].map((it, i) => (
        <span key={i} className="mx-8 inline-flex items-center gap-8 text-ivory/40 uppercase tracking-[0.25em] text-sm font-display">
          {it} <span className="text-gold text-lg">✦</span>
        </span>
      ))}
    </div>
  </div>
);

export const CourseCard = ({ course, index = 0 }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link
      to={`/corsi/${course.slug}`}
      data-testid={`course-card-${course.slug}`}
      className="lift group block h-full bg-white border border-black/10 rounded-sm p-7 hover:border-gold"
    >
      <div className="flex items-start justify-between">
        <h3 className="font-display text-2xl font-medium text-ink-800">{course.name}</h3>
        <ArrowUpRight className="text-ink-800/30 group-hover:text-gold transition-colors" size={22} />
      </div>
      <p className="mt-3 text-ink-800/60 text-sm leading-relaxed">{course.short}</p>
      <div className="mt-6 pt-5 border-t border-black/5 space-y-1.5">
        {course.schedule.slice(0, 2).map((s, i) => (
          <div key={i} className="flex justify-between text-xs text-ink-800/60">
            <span>{s.days}</span><span className="font-semibold text-ink-800">{s.time}</span>
          </div>
        ))}
        <div className="flex justify-between text-xs pt-1">
          <span className="text-gold font-semibold tracking-wide">Durata 50 min</span>
          <span className="text-ink-800/50 group-hover:text-gold transition-colors">Scopri il corso →</span>
        </div>
      </div>
    </Link>
  </Reveal>
);

export const ScheduleTable = ({ schedule }) => (
  <div className="rounded-sm border border-black/10 overflow-hidden" data-testid="schedule-table">
    <div className="grid grid-cols-2 bg-ink-800 text-ivory text-xs uppercase tracking-[0.2em] font-semibold">
      <div className="px-5 py-4">Giorni</div>
      <div className="px-5 py-4 text-right">Orario</div>
    </div>
    {schedule.map((s, i) => (
      <div key={i} className={`grid grid-cols-2 text-sm ${i % 2 ? "bg-sand/60" : "bg-white"}`}>
        <div className="px-5 py-4 text-ink-800/80">{s.days}</div>
        <div className="px-5 py-4 text-right font-semibold text-ink-800">{s.time}</div>
      </div>
    ))}
  </div>
);
